import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { POD_STATUS_COLORS, CompFreeBreakdown } from '../podDashboard.types';
import StripeLink from './StripeLink';

interface Props {
    compFree: CompFreeBreakdown;
}

const headerCellSx = {
    color: 'text.secondary',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    border: 0,
    py: 1,
};

const bodyCellSx = { fontSize: '0.85rem', borderColor: 'grey.100', py: 1.25 };

/** Small summary chip: a count under a label, with a colored accent. */
const SummaryStat = ({ label, value }: { label: string; value: number }) => (
    <Box
        sx={{
            flex: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 1.5,
        }}
    >
        <Typography sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
            {value.toLocaleString('en-US')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{label}</Typography>
    </Box>
);

/**
 * Comped/Free drill-down: marked-comped vs. unmarked-free totals, plus a per-org
 * table of how many comped and free (unpaid) pods each organization holds.
 */
const CompFreeDrilldown = ({ compFree }: Props) => (
    <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <SummaryStat label="Marked comped" value={compFree.marked} />
            <SummaryStat label="Unmarked / free" value={compFree.unmarked} />
        </Box>

        {compFree.orgs.length > 0 ? (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>ORGANIZATION</TableCell>
                        <TableCell sx={headerCellSx} align="right">
                            MARKED COMPED
                        </TableCell>
                        <TableCell sx={headerCellSx} align="right">
                            UNMARKED / FREE
                        </TableCell>
                        <TableCell sx={headerCellSx} align="right">
                            TOTAL
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {compFree.orgs.map((row) => (
                        <TableRow key={row.holder} hover>
                            <TableCell sx={bodyCellSx}>
                                {row.holder}
                                <StripeLink id={row.stripeCustomerId} entity="customers" />
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {row.comped.toLocaleString('en-US')}
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {row.free.toLocaleString('en-US')}
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {(row.comped + row.free).toLocaleString('en-US')}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }}>
                            Total
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {compFree.marked.toLocaleString('en-US')}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {compFree.unmarked.toLocaleString('en-US')}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {(compFree.marked + compFree.unmarked).toLocaleString('en-US')}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        ) : (
            <Typography
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    borderTop: `2px solid ${POD_STATUS_COLORS.comp_free}`,
                    pt: 1,
                }}
            >
                No comped or free pods.
            </Typography>
        )}
    </Box>
);

export default CompFreeDrilldown;
